import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "antd";
import { Eye, PencilSimple, Plus, Trash } from "@phosphor-icons/react";
import AccountOrderGrid, { AccountOrderMenu, AccountOrderColumn } from "./AccountOrderGrid.tsx";
import { AccountOrder, accountOrderStatusLabel, readAccountOrders, deleteAccountOrder } from "./accountOrderService.ts";
import { canWriteAgencyCommerce, isCompanySalesAgent } from "../../../utils/agencySession.ts";
import { t, getLocale, useStoreLanguage } from "../../../features/store-language/i18n.ts";
import AppFeedback from "../../shared/AppFeedback.tsx";
import { compactOrderNumber } from "./orderNumber.ts";

export default function AccountOrdersPage() {
  const language = useStoreLanguage();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<AccountOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(1000);
  const [revision, setRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<number>();
  const locked = useRef(false);
  useEffect(function () {
    let active = true;
    setLoading(true);
    readAccountOrders(pageSize).then(function (result) {
      if (active) { setOrders(result.items); setTotal(result.total); }
    }).catch(function (error) {
      if (active) { setOrders([]); AppFeedback.httpError(error); }
    }).finally(function () { if (active) setLoading(false); });
    return function () { active = false; };
  }, [language, pageSize, revision]);

  async function remove(order: AccountOrder) {
    if (locked.current || !canWriteAgencyCommerce()) return;
    locked.current = true;
    try {
      const result = await AppFeedback.confirmDestructive({
        title: t("حذف سفارش"), text: t("سفارش شماره {{value0}} حذف شود؟", { value0: order.id }),
        confirmButtonText: t("بله، حذف شود"), cancelButtonText: t("انصراف"), showCancelButton: true,
      });
      if (!result.isConfirmed) return;
      setDeleting(order.id);
      await deleteAccountOrder(order);
      setRevision(function (value) { return value + 1; });
    } catch (error) { AppFeedback.httpError(error); }
    finally { setDeleting(undefined); locked.current = false; }
  }

  const columns: AccountOrderColumn<AccountOrder>[] = [
    { colId: "no", headerName: t("ردیف"), valueGetter: "node.rowIndex + 1", sortable: false, filter: false, enableRowGroup: false },
    { field: "id", headerName: t("شماره سفارش"), sortable: true, floatingFilter: true, enableRowGroup: true },
    { field: "orderNumber", headerName: t("کد سفارش"), mobileEssential: true, sortable: true, floatingFilter: true, enableRowGroup: true,
      valueGetter: function (params) { return compactOrderNumber(params.data?.orderNumber); } },
    { field: "title", headerName: t("عنوان سفارش"), mobileEssential: true, sortable: true, floatingFilter: true, enableRowGroup: true },
    ...(isCompanySalesAgent() ? [
      { field: "agencyTitle" as const, headerName: t("نام نماینده"), mobileEssential: true, sortable: true, floatingFilter: true, enableRowGroup: true },
    ] : []),
    { field: "placedByDisplayName", headerName: t("ثبت‌کننده سفارش"), sortable: true, floatingFilter: true, enableRowGroup: true },
    { field: "cancellationReason", headerName: t("دلیل لغو"), sortable: true, floatingFilter: true, enableRowGroup: true },
    { field: "description", headerName: t("توضیحات سفارش"), sortable: true, floatingFilter: true, enableRowGroup: true },
    { field: "orderedAt", headerName: t("تاریخ ثبت"), sortable: true, floatingFilter: true, enableRowGroup: true,
      valueFormatter: function (params) { return params.value ? new Date(params.value).toLocaleDateString(getLocale()) : ""; } },
    { field: "status", headerName: t("وضعیت"), mobileEssential: true, sortable: true, floatingFilter: true, enableRowGroup: true,
      valueGetter: function (params) { return params.data ? accountOrderStatusLabel(params.data.status) : ""; } },
    { field: "itemCount", headerName: t("تعداد آیتم‌ها"), sortable: true, floatingFilter: true, enableRowGroup: true, enableValue: true, enablePivot: true },
    { colId: "operation", field: "operation" as keyof AccountOrder, headerName: t("عملیات"), pinned: "left", sortable: false, filter: false, floatingFilter: false, suppressSharedOperationMenu: true,
      cellRenderer: function (params) {
        const order: AccountOrder | undefined = params.data;
        if (!order) return null;
        const canWrite = canWriteAgencyCommerce();
        const editable = canWrite && (order.status === "Draft" || order.status === "Confirmed");
        return (
          <AccountOrderMenu
            pending={deleting === order.id}
            disabled={deleting !== undefined}
            items={[
              { key: "open", icon: editable ? <PencilSimple size={17} /> : <Eye size={17} />, label: editable ? t("ویرایش") : t("مشاهده"), onClick: function () { navigate(`/account/orders/${order.id}`); } },
              ...(canWrite && order.status === "Draft" ? [{ key: "delete", icon: <Trash size={17} />, label: t("حذف"), danger: true, onClick: function () { return remove(order); } }] : []),
            ]}
          />
        );
      } },
  ];
  return (
    <section className="account-orders-page">
      <AccountOrderGrid
        id="account-customer-orders-v1"
        title={t("سفارش‌ها")}
        filterBy="orderNumber"
        rows={orders}
        columns={columns}
        loading={loading || deleting !== undefined}
        onRefresh={function () { setRevision(function (value) { return value + 1; }); }}
        actions={canWriteAgencyCommerce() ? (
          <Button
            type="primary"
            icon={<Plus size={18} />}
            disabled={deleting !== undefined}
            onClick={function () { navigate("/account/orders/create"); }}
          >
            {t("ایجاد سفارش")}
          </Button>
        ) : null}
        footer={total > orders.length ? (
          <Button
            disabled={loading}
            loading={loading}
            onClick={function () { setPageSize(0); }}
          >
            {t("نمایش همه داده‌ها")}
          </Button>
        ) : null}
      />
    </section>
  );
}
